/**
 * Профиль игрока.
 *
 * Своя страница — рабочая: здесь меняют имя, аватар, язык и адрес почты.
 * Чужая — только смотровая. Статистика сведена к двум величинам, которые
 * действительно говорят об игроке: насколько точно он попадает по месту и по
 * году. Сколько квестов пройдено и сколько очков набрано, интересно куда
 * меньше, поэтому история игр вынесена на отдельную вкладку и не занимает
 * первый экран.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router-dom";

import {
  useAnnouncements,
  useFavoriteQuests,
  useInvitePlayer,
  useMyFeedback,
  useMyQuests,
  usePlayerProfile,
} from "../api/queries";
import type { Announcement, Feedback, PlayerProfile, Quest, RecentPlayer } from "../api/types";
import { ProfileEditor } from "../components/ProfileEditor";
import { QuestCard } from "../components/QuestCard";
import { PlayerLink } from "../components/PlayerLink";
import { Avatar } from "../components/Avatar";
import { ErrorState, ScoreBar, Skeleton } from "../components/ui";
import { formatDate, formatDistance, formatScore } from "../lib/format";
import { useAuthStore } from "../stores/authStore";

type Tab = "overview" | "history" | "quests" | "favorites" | "announcements" | "messages";

export function ProfilePage() {
  const { t } = useTranslation();
  const { userId } = useParams<{ userId?: string }>();
  const currentUser = useAuthStore((state) => state.user);

  const isOwnProfile = !userId || userId === currentUser?.id;
  const profile = usePlayerProfile(userId ?? "me");
  const myQuests = useMyQuests(isOwnProfile);
  const favorites = useFavoriteQuests(isOwnProfile);
  const announcements = useAnnouncements(false, isOwnProfile);
  const invite = useInvitePlayer();
  // Переписка с модератором — только своя: на чужом профиле её не запрашиваем.
  const feedback = useMyFeedback(isOwnProfile).data?.items ?? [];

  // Адрес /favorites открывает профиль сразу нужной вкладкой: пункт меню
  // ведёт к избранному, а не «куда-то в профиль».
  const wantsFavorites = useLocation().pathname === "/favorites";
  const [tab, setTab] = useState<Tab>(wantsFavorites ? "favorites" : "overview");
  const [editing, setEditing] = useState(false);

  if (profile.isPending) return <Skeleton className="h-96" />;
  if (profile.isError || !profile.data) {
    return <ErrorState message={t("profile.notFound")} onRetry={() => void profile.refetch()} />;
  }

  const data = profile.data;
  // Вкладка появляется, только когда переписка есть: пустая она ничего
  // не сообщает, а место в ряду занимает.
  // История игр — личное: по ней видно, какие квесты человек уже проходил.
  // В чужом профиле остаётся только обзор с агрегатами; сервер историю туда
  // и не отдаёт, так что вкладка была бы пустой.
  const tabs: Tab[] = isOwnProfile
    ? [
        "overview",
        "history",
        "quests",
        "favorites",
        "announcements",
        ...(feedback.length > 0 ? (["messages"] as Tab[]) : []),
      ]
    : ["overview"];

  return (
    <div className="space-y-6">
      <div className="panel p-6">
        <div className="flex flex-wrap items-center gap-4">
          <Avatar
            url={data.user?.avatarUrl}
            username={data.user?.username ?? ""}
            role={data.user?.role}
            className="h-20 w-20 text-2xl"
          />

          <div className="min-w-0 flex-1">
            <span className="label">{t("profile.title")}</span>
            <h1 className="truncate font-sans text-3xl text-ink-50">{data.user?.username}</h1>
            {isOwnProfile && currentUser?.email && (
              <p className="mt-1 truncate text-sm text-ink-500">{currentUser.email}</p>
            )}
          </div>

          {isOwnProfile ? (
            <button
              type="button"
              className="btn-secondary shrink-0"
              onClick={() => setEditing((value) => !value)}
            >
              {editing ? t("common.close") : t("profile.edit")}
            </button>
          ) : (
            // Позвать поиграть — то, ради чего в чужой профиль чаще всего и
            // заходят. Комната заведётся сама: требовать создать её заранее
            // значит отправить человека сюда во второй раз.
            currentUser &&
            data.user?.id && (
              <button
                type="button"
                className="btn-primary shrink-0"
                disabled={invite.isPending || invite.isSuccess}
                onClick={() => invite.mutate(data.user!.id!)}
              >
                {invite.isSuccess ? t("invite.sent") : t("invite.button")}
              </button>
            )
          )}
        </div>

        {isOwnProfile && editing && (
          <ProfileEditor className="mt-6" onDone={() => setEditing(false)} />
        )}
      </div>

      <nav className="flex gap-1 border-b border-ink-800">
        {tabs.map((name) => (
          <button
            key={name}
            type="button"
            className={`-mb-px border-b-2 px-4 py-2 text-sm transition-colors ${
              tab === name
                ? "border-amber-400 text-amber-300"
                : "border-transparent text-ink-400 hover:text-ink-200"
            }`}
            onClick={() => setTab(name)}
          >
            {t(`profile.tab.${name}`)}
          </button>
        ))}
      </nav>

      {tab === "overview" && <Overview data={data} own={isOwnProfile} />}
      {tab === "history" && <History data={data} />}
      {tab === "messages" && isOwnProfile && <Messages items={feedback} />}

      {tab === "quests" && isOwnProfile && (
        <MyQuests quests={myQuests.data?.quests ?? []} />
      )}

      {tab === "favorites" && isOwnProfile && (
        <Favorites quests={favorites.data?.quests ?? []} />
      )}

      {tab === "announcements" && isOwnProfile && (
        <Announcements items={announcements.data?.announcements ?? []} />
      )}
    </div>
  );
}

function Overview({ data, own }: { data: PlayerProfile; own: boolean }) {
  const { t } = useTranslation();
  const recent = data.recentPlayers ?? [];

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <Accuracy
          label={t("profile.placeAccuracy")}
          value={formatDistance(data.averageDistanceKm ?? 0)}
          hint={t("profile.averageMiss")}
        />
        <Accuracy
          label={t("profile.yearAccuracy")}
          value={t("format.years", { count: Math.round(data.averageYearDiff ?? 0) })}
          hint={t("profile.averageError")}
        />
      </div>

      {own && (
        <div>
          <h2 className="mb-3 font-sans text-xl text-ink-50">{t("profile.recentPlayers")}</h2>

          {recent.length === 0 ? (
            <p className="panel p-5 text-sm text-ink-400">{t("profile.noRecentPlayers")}</p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              {recent.map((player) => (
                <RecentPlayerRow key={player.user?.id} player={player} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RecentPlayerRow({ player }: { player: RecentPlayer }) {
  const { t } = useTranslation();

  return (
    <Link
      to={`/players/${player.user?.id}`}
      className="panel flex items-center gap-3 px-4 py-3 transition-colors hover:border-amber-400/40"
    >
      <Avatar
        url={player.user?.avatarUrl}
        username={player.user?.username ?? ""}
        role={player.user?.role}
        className="h-10 w-10"
      />
      <div className="min-w-0 flex-1">
        <PlayerLink
          userId={player.user?.id}
          username={player.user?.username}
          className="block truncate text-sm text-ink-100"
        />
        <div className="text-xs text-ink-500">
          {t("profile.gamesTogether", { count: player.gamesTogether ?? 0 })} ·{" "}
          {formatDate(player.lastPlayedAt)}
        </div>
      </div>
    </Link>
  );
}

function History({ data }: { data: PlayerProfile }) {
  const { t } = useTranslation();
  const history = data.history ?? [];

  if (history.length === 0) {
    return (
      <div className="panel p-6 text-center text-sm text-ink-400">
        {t("profile.noGames")}{" "}
        <Link to="/quests" className="text-amber-300 hover:text-amber-200">
          {t("profile.pickQuest")}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat
          label={t("profile.questsPlayed")}
          value={String(data.questsPlayed ?? 0)}
          hint={t("profile.questsHint", { count: data.questsPlayed ?? 0 })}
        />
        <Stat
          label={t("profile.roundsPlayed")}
          value={String(data.roundsPlayed ?? 0)}
          hint={t("profile.roundsHint", { count: data.roundsPlayed ?? 0 })}
        />
        <Stat
          label={t("profile.best")}
          value={formatScore(data.bestQuestScore ?? 0)}
          hint={t("profile.perQuest")}
        />
        <Stat
          label={t("profile.average")}
          value={formatScore(Math.round(data.averageRoundScore ?? 0))}
          hint={t("profile.perRound")}
        />
      </dl>

      <div className="space-y-2">
        {history.map((entry) => {
          const score = entry.totalScore ?? 0;
          const max = entry.maxScore ?? 1;

          return (
            <Link
              key={entry.sessionId}
              to={`/sessions/${entry.sessionId}/results`}
              className="panel flex items-center gap-4 px-4 py-3 transition-colors hover:border-amber-400/40"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-ink-100">{entry.questTitle}</div>
                <div className="mt-1 text-xs text-ink-500">{formatDate(entry.playedAt)}</div>
                <ScoreBar value={score} max={max} className="mt-2 h-1" />
              </div>
              <div className="shrink-0 text-right">
                <div className="tabular-nums text-lg text-amber-300">{formatScore(score)}</div>
                <div className="text-[11px] text-ink-500">
                  {t("round.outOf", { max: formatScore(max) })}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

// Анонсы списком: всплывающее окно человек мог закрыть не читая, и вернуться
// к нему должно быть где.
function Announcements({ items }: { items: Announcement[] }) {
  const { t } = useTranslation();

  if (items.length === 0) {
    return <div className="panel p-6 text-center text-sm text-ink-400">{t("announcements.empty")}</div>;
  }

  return (
    <div className="space-y-3">
      {items.map((item) => (
        <article key={item.id} className="panel overflow-hidden">
          {item.coverImageUrl && (
            <img src={item.coverImageUrl} alt="" className="aspect-[16/6] w-full object-cover" />
          )}
          <div className="p-4 sm:p-5">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="font-sans text-lg text-ink-50">{item.title}</h3>
              {/* Дата публикации, а не создания: текст пишут заранее. */}
              <span className="text-xs text-ink-500">
                {[formatDate(item.publishedAt ?? item.createdAt), item.author?.username]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </div>
            <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-ink-300">{item.body}</p>
          </div>
        </article>
      ))}
    </div>
  );
}

// Избранное показывается теми же карточками, что и каталог: это те же самые
// квесты, и узнавать их человек должен по тому же виду.
function Favorites({ quests }: { quests: Quest[] }) {
  const { t } = useTranslation();

  if (quests.length === 0) {
    return <div className="panel p-6 text-center text-sm text-ink-400">{t("favorites.empty")}</div>;
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {quests.map((quest) => (
        <QuestCard key={quest.id} quest={quest} />
      ))}
    </div>
  );
}

function MyQuests({ quests }: { quests: { id?: string; title?: string; status?: string; moderationComment?: string }[] }) {
  const { t } = useTranslation();

  if (quests.length === 0) {
    return (
      <div className="panel p-6 text-center text-sm text-ink-400">
        {t("profile.noQuests")}{" "}
        <Link to="/create" className="text-amber-300 hover:text-amber-200">
          {t("profile.createQuest")}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {quests.map((quest) => (
        <div
          key={quest.id}
          className="panel flex items-center justify-between gap-3 px-4 py-3 transition-colors
                     hover:border-amber-400/40"
        >
          <Link to={`/quests/${quest.id}`} className="min-w-0 flex-1">
            <div className="truncate text-sm text-ink-100">{quest.title}</div>
            {quest.moderationComment && (
              <div className="mt-0.5 truncate text-xs text-ink-500">
                {t("profile.moderator", { comment: quest.moderationComment })}
              </div>
            )}
          </Link>

          <StatusChip status={quest.status} />

          {/* Архивную версию править нечем: её уже заменила новая. */}
          {quest.status !== "CONTENT_STATUS_ARCHIVED" && (
            <Link
              to={`/quests/${quest.id}/edit`}
              className="btn-ghost shrink-0 px-2.5 py-1 text-xs"
            >
              {t("profile.editQuest")}
            </Link>
          )}
        </div>
      ))}
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="rounded-xl border border-ink-800 bg-ink-850/50 p-3">
      <dt className="text-[11px] uppercase tracking-wider text-ink-500">{label}</dt>
      <dd className="mt-1 font-sans text-2xl text-amber-300">{value}</dd>
      <dd className="text-[11px] text-ink-500">{hint}</dd>
    </div>
  );
}

function Accuracy({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="rounded-xl border border-ink-800 bg-ink-850/50 p-4">
      <div className="label mb-1">{label}</div>
      <div className="font-sans text-3xl text-ink-50">{value}</div>
      <div className="mt-1 text-xs text-ink-500">{hint}</div>
    </div>
  );
}

function StatusChip({ status }: { status?: string }) {
  const { t } = useTranslation();
  const map: Record<string, { key: string; className: string }> = {
    CONTENT_STATUS_PUBLISHED: { key: "quest.status.published", className: "border-sage/40 text-sage" },
    CONTENT_STATUS_DRAFT: { key: "quest.status.draft", className: "border-amber-400/40 text-amber-300" },
    CONTENT_STATUS_REJECTED: { key: "quest.status.rejected", className: "border-rust/50 text-rust" },
    CONTENT_STATUS_ARCHIVED: { key: "quest.status.archived", className: "border-ink-600 text-ink-400" },
  };
  const item = status ? map[status] : undefined;
  if (!item) return null;

  return <span className={`chip shrink-0 ${item.className}`}>{t(item.key)}</span>;
}

/**
 * Переписка с модератором.
 *
 * Показывается только в своём профиле: это личные сообщения, а не публичная
 * лента. Ответ идёт под самим обращением — иначе непонятно, на что он.
 */
function Messages({ items }: { items: Feedback[] }) {
  const { t } = useTranslation();

  if (items.length === 0) {
    return <div className="panel p-6 text-center text-sm text-ink-400">{t("feedback.empty")}</div>;
  }

  return (
    <div className="space-y-3">
      {items.map((item) => (
        <div key={item.id} className="panel p-4">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="chip">{t(`feedback.kind.${item.kind}`)}</span>
            <span
              className={`chip ${
                item.status === "FEEDBACK_STATUS_ANSWERED"
                  ? "border-sage/50 text-sage"
                  : "text-ink-400"
              }`}
            >
              {t(`feedback.status.${item.status}`)}
            </span>
            <span className="text-ink-500">{formatDate(item.createdAt)}</span>
            {item.questTitle && <span className="truncate text-ink-500">{item.questTitle}</span>}
          </div>

          <p className="mt-2 whitespace-pre-line text-sm text-ink-200">{item.message}</p>

          {(item.replies ?? []).map((reply) => (
            <div key={reply.id} className="mt-3 rounded-xl border border-amber-400/25 bg-amber-400/5 p-3">
              <span className="label mb-1">{t("feedback.answer")}</span>
              <p className="whitespace-pre-line text-sm text-ink-100">{reply.message}</p>
              <p className="mt-1 text-xs text-ink-500">{formatDate(reply.createdAt)}</p>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
